<template>
  <div class="u-shop-product-controls">
    <b-numberinput
      v-if="showQuantityControls"
      :value="configProvider.quantities[configProvider.productId]"
      :step="configProvider.quantityStep"
      :min="configProvider.quantityStep"
      :max="configProvider.maxQuantity ? configProvider.maxQuantity : undefined"
      :disabled="isProcessing"
      :editable="false"
      expanded
      type="is-light"
      @input="inputDebounced"
    />

    <b-button
      type="is-dark"
      native-type="submit"
      icon-left="shopping-basket"
      expanded
      :disabled="isDisabled"
      :loading="isProcessing || configProvider.isProcessing"
    >
      <i18n path="_action.action_basket_item">
        <template #action>{{ cta }}</template>
        <!-- Price -->
        <template v-if="!showProductTrialMessage || !hidePrices" #price
          >-
          {{
            $store.getters["utils/money/trimTrailingZeros"](
              configProvider.configTotalFormatted
            )
          }}</template
        >
        <!-- Tax summary -->
        <template
          v-if="
            basketData.isTaxRegistered &&
            (!showProductTrialMessage || !hidePrices)
          "
          #taxSummary
        >
          <em>{{ `(${$tc("_.excl_incl_tax", configProvider.taxType)})` }}</em>
        </template>
      </i18n>
    </b-button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { validate } from "vee-validate/dist/vee-validate.full.esm";
import { ProductOrderTypes } from "@/data/enums";
import type { PropType } from "vue";
import type { IBasketProviderData } from "@/models/providers/baskets";

export default defineComponent({
  name: "ProductControl",
  inject: {
    $basketData: {
      from: "basketProviderData",
      type: Function as PropType<IBasketProviderData>
    }
  },
  props: {
    isProcessing: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    basketProductId: { type: [String, Boolean, Number], default: null },
    configProvider: { type: Object as PropType<any>, default: null },
    showProductTrialMessage: { type: Boolean, default: false },
    hidePrices: { type: Boolean, default: false }
  },
  data: () => ({
    inputDebounced: null as any
  }),
  computed: {
    basketData() {
      return this.$basketData();
    },
    showQuantityControls() {
      return this.configProvider.orderType === ProductOrderTypes.QUANTITY_BASED;
    },
    cta() {
      return this.basketProductId
        ? this.$t("_action.update")
        : this.$store.getters["ui/isMobile"]
        ? this.$t("_action.add")
        : this.$t("_action.add_to_basket");
    }
  },
  created() {
    this.inputDebounced = this.$_.debounce(this.quantityChanged, 250);
  },
  methods: {
    async quantityChanged(value) {
      const result = await validate(
        this.configProvider.quantities[this.configProvider.productId],
        this.$_.concat(
          [
            `min_value:${this.configProvider.quantityStep}`,
            `divisible_by:${this.configProvider.quantityStep}`
          ],
          this.configProvider.maxQuantity
            ? [`max_value:${this.configProvider.maxQuantity}`]
            : []
        ).join("|")
      );
      if (result.valid)
        this.$set(
          this.configProvider.quantities,
          this.configProvider.productId,
          value
        );
    }
  }
});
</script>

<style lang="scss" scoped>
.u-shop-product-controls {
  display: grid;
  @include mobile {
    grid-row-gap: 0.75rem;
  }
  @include tablet {
    grid-template-columns: 10rem 1fr;
    grid-column-gap: 0.75rem;
  }

  & > :only-child {
    grid-column: 1 / span 2;
  }
}
</style>
